/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';
import { LearnerProfile_OccupationPreferenceLevel as LearnerProfileOccupationPreferenceLevel } from '__generated__/graphql-types';
import type {
  LearnerProfile_Industry as LearnerProfileOccupationIndustry,
  LearnerProfile_OccupationPreference as LearnerProfileOccupationPreference,
} from '__generated__/graphql-types-do-not-use';
import { partition, uniqueId } from 'lodash';

import { Button, CheckboxCard, CheckboxGroup, Dialog, Divider, InlineNotification } from '@coursera/cds-core';
import { AddIcon } from '@coursera/cds-icons';

import type { LearnerProfile as Profile } from 'bundles/account-profile/common/types';
import { OccupationRolesContext } from 'bundles/account-profile/components/recruiting-profile-viewer/OccupationRolesProvider';
import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import CurrentlyHiringNotification from 'bundles/account-profile/components/recruiting-profile-viewer/components/work-preferences/CurrentlyHiringNotification';
import RolePreferencesSelect from 'bundles/account-profile/components/recruiting-profile-viewer/components/work-preferences/RolePreferencesSelect';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import TrackedButton from 'bundles/page/components/TrackedButton';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/account-profile';

const styles = {
  divider: css`
    margin-top: var(--cds-spacing-300);
  `,
  checkboxGroup: css`
    width: 100%;
    margin-top: var(--cds-spacing-300);
  `,
  withMarginBottom: css`
    margin-bottom: var(--cds-spacing-300);
  `,
  saveError: css`
    margin-top: var(--cds-spacing-200);
  `,
  currentlyHiring: css`
    margin-top: var(--cds-spacing-300);
  `,
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

type DesiredRoleBase = {
  id: string;
  isDesired: boolean;
  level: LearnerProfileOccupationPreferenceLevel;
  industry?: LearnerProfileOccupationIndustry | null;
  name: string;
};

type DesiredRole = DesiredRoleBase & {
  uniqueId: string;
};

type PartialDesiredRole = Partial<DesiredRoleBase> & {
  uniqueId: string;
};

const MAX_DESIRED_ROLES = 5;

const PREFERENCE_LEVELS = [
  LearnerProfileOccupationPreferenceLevel.Intern,
  LearnerProfileOccupationPreferenceLevel.JuniorIndividualContributor,
  LearnerProfileOccupationPreferenceLevel.SeniorIndividualContributor,
  LearnerProfileOccupationPreferenceLevel.ManagerialLeadershipRole,
  LearnerProfileOccupationPreferenceLevel.Director,
  LearnerProfileOccupationPreferenceLevel.ExecutiveManagement,
];

const E2E_SELECTORS = {
  WORK_PREF_DIALOG_SAVE: 'profile-work-pref-dialog-save',
} as const;

const EditWorkPreferencesModal = ({ isOpen, onClose }: Props) => {
  const { updateLearnerProfile, profileState = {}, isProfileLoading } = useContext(ProfileContext);

  const { openToRemoteWork, willingToRelocate, occupationPreferences = [] } = profileState;
  const { showPreferredWorkLocationFields, enableHiringSolutionsOptInFlow } =
    ('displaySettings' in profileState && profileState.displaySettings) || {};
  const { idToRolesMap, activelyHiringOccupationOptions } = useContext(OccupationRolesContext);
  const allTalentPipelineIds = new Set(activelyHiringOccupationOptions.map((occ) => occ.value));
  const getRoleName = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (role: LearnerProfileOccupationPreference) => {
      return 'id' in role.occupation
        ? idToRolesMap.get(role.occupation.id)?.occupationName ?? ''
        : role.occupation.name;
    },
    [idToRolesMap]
  );
  const [isOpenToRemoteWork, setIsOpenToRemoteWork] = useState<boolean | null | undefined>(openToRemoteWork);
  const [isWillingToRelocate, setIsWillingToRelocate] = useState<boolean | null | undefined>(willingToRelocate);
  const [selectedRolesMap, setSelectedRolesMap] = useState<PartialDesiredRole[]>([]);
  const [rolePreferenceErrors, setRolePreferenceErrors] = useState<{ [id: string]: string }>({});
  const [onSaveError, setOnSaveError] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const rolePreferenceRefs = useRef<Array<HTMLParagraphElement | null>>([]);
  const saveErrorRef = useRef<HTMLDivElement | null>(null);
  const optOutOfTalentPipelineRef = useRef<HTMLDivElement | null>(null);
  const hasOptionToDeleteRoles = selectedRolesMap.length > 0;

  const scrollToFirstError = ({
    hasSaveError,
    hasRolePreferenceError,
    rolePreferenceIndex,
  }: {
    hasRolePreferenceError?: boolean;
    isOptingOutOfTalentPipelines?: boolean;
    hasSaveError?: boolean;
    rolePreferenceIndex?: number;
  }) => {
    if (hasRolePreferenceError) {
      rolePreferenceRefs.current[rolePreferenceIndex ?? 0]?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    } else if (hasSaveError) {
      saveErrorRef?.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
    }
  };

  const removeRoleData = (idx: number) => {
    const newResults = [...selectedRolesMap.slice(0, idx), ...selectedRolesMap.slice(idx + 1)];
    if (newResults.length === 0) {
      newResults.push({
        uniqueId: uniqueId(),
      });
    }
    setSelectedRolesMap(newResults);
  };

  const onDeleteDesiredRole = (idx: number) => {
    removeRoleData(idx);
  };

  const validateSelectError = (targetRole: PartialDesiredRole) => {
    let targetIndKey = '';
    if (targetRole.industry) {
      targetIndKey = 'id' in targetRole.industry ? targetRole.industry.id : targetRole.industry.name;
    }
    let targetIndex = 0;

    const hasError =
      selectedRolesMap.filter((role, idx) => {
        // Ignore self, otherwise its a duplicate when selecting an existing value
        if (role.uniqueId === targetRole.uniqueId) {
          targetIndex = idx;
          return false;
        }

        let indKey = '';
        if (role.industry) {
          indKey = 'id' in role.industry ? role.industry.id : role.industry.name;
        }

        return role.id === targetRole.id && indKey === targetIndKey && role.level === targetRole.level;
      }).length > 0;

    if (hasError) {
      setRolePreferenceErrors((prevState) => ({
        ...prevState,
        [targetRole.uniqueId]: _t('Please select unique values for desired role'),
      }));
      scrollToFirstError({ hasRolePreferenceError: true, rolePreferenceIndex: targetIndex });
    } else {
      setRolePreferenceErrors({});
    }

    return hasError;
  };

  const handleOnSelectRole = (newId: string, idx: number) => {
    const isTalentPipeline = allTalentPipelineIds.has(newId);
    const nextLevel = isTalentPipeline
      ? LearnerProfileOccupationPreferenceLevel.JuniorIndividualContributor
      : selectedRolesMap[idx]?.level ?? LearnerProfileOccupationPreferenceLevel.NotApplicable;

    if (
      // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      validateSelectError({
        ...selectedRolesMap[idx],
        id: newId,
        level: nextLevel,
      })
    ) {
      return;
    }

    setSelectedRolesMap((prevMap) => {
      const newState = [...prevMap];
      // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      newState[idx] = {
        ...newState[idx],
        id: newId,
        isDesired: !isTalentPipeline,
        name: idToRolesMap.get(newId)?.occupationName ?? newId,
        level: nextLevel,
      };
      return newState;
    });
  };

  const handleOnSelectIndustry = (idx: number, id: string, newIndustry: string, isKey?: boolean) => {
    const newIndustryObject = isKey
      ? {
          id: newIndustry,
        }
      : {
          name: newIndustry,
        };

    if (
      // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      validateSelectError({
        ...selectedRolesMap[idx],
        industry: newIndustryObject,
      })
    ) {
      return;
    }

    // Remove the old level and replace it with the new level
    setSelectedRolesMap((prevState) => {
      const newState = [...prevState];
      // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      newState[idx] = {
        ...newState[idx],
        industry: newIndustryObject,
      };
      return newState;
    });
  };

  const handleOnSelectLevel = (idx: number, id: string, newLevel: LearnerProfileOccupationPreferenceLevel) => {
    if (
      // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      validateSelectError({
        ...selectedRolesMap[idx],
        level: newLevel,
      })
    ) {
      return;
    }

    setSelectedRolesMap((prevState) => {
      const newState = [...prevState];
      // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      newState[idx] = {
        ...newState[idx],
        level: newLevel ?? LearnerProfileOccupationPreferenceLevel.NotApplicable,
      };
      return newState;
    });
  };

  const setUpLocationPreference = () => {
    setIsOpenToRemoteWork(openToRemoteWork);
    setIsWillingToRelocate(willingToRelocate);
  };

  const setUpRoles = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    // FIXME: existing react-hooks/exhaustive-deps violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
    () => {
      if (isProfileLoading) return;

      const currentOtherRoles: PartialDesiredRole[] = [];

      occupationPreferences.forEach((pref) => {
        const prefKey = 'id' in pref.occupation ? pref.occupation.id : pref.occupation.name;

        const isTalentPipeline = allTalentPipelineIds.has(prefKey);
        const roleLevel = isTalentPipeline
          ? LearnerProfileOccupationPreferenceLevel.JuniorIndividualContributor
          : pref?.level ?? LearnerProfileOccupationPreferenceLevel.NotApplicable;

        currentOtherRoles.push({
          id: prefKey,
          uniqueId: uniqueId(),
          isDesired: !isTalentPipeline,
          name: getRoleName(pref),
          level: roleLevel,
          industry: pref?.industry,
        });
      });

      if (currentOtherRoles.length === 0) {
        currentOtherRoles.push({
          uniqueId: uniqueId(),
        });
      }

      setSelectedRolesMap(currentOtherRoles);
      // TODO: Adding allTalentPipelineIds causes infinite loop
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activelyHiringOccupationOptions, getRoleName, isProfileLoading, occupationPreferences]
  );

  useEffect(() => {
    setUpRoles();
  }, [setUpRoles]);

  const handleOnAdd = () => {
    setSelectedRolesMap((prevState) => {
      return [
        ...prevState,
        {
          uniqueId: uniqueId(),
        },
      ];
    });
  };

  // CDS Modals don't actually unmount. They just hide using CSS. We need to call setUpRoles & setUpLocationPreference in order to reset the data since the modal doesn't unmount
  const handleOnClose = () => {
    setUpRoles();
    setUpLocationPreference();
    setOnSaveError(undefined);
    setRolePreferenceErrors({});
    onClose();
  };

  const handleOnSave = async () => {
    setIsLoading(true);

    const missingInfo =
      selectedRolesMap.filter((role) => {
        return !role.id;
      }).length > 0;
    if (missingInfo && selectedRolesMap.length > 1) {
      setOnSaveError(_t('Please fill out all roles'));
      setIsLoading(false);
      scrollToFirstError({ hasSaveError: true });
      return;
    }

    try {
      const filteredRoles = selectedRolesMap.filter((role) => role.id && role.level !== undefined);
      const castedRoles = filteredRoles.map((role) => role as DesiredRole);
      // False check is so CHS returns first, Desired roles second
      const partitionedRoles = partition(castedRoles, (p) => !p.isDesired);

      const formattedOccupationObjects: LearnerProfileOccupationPreference[] = [
        ...partitionedRoles[0],
        ...partitionedRoles[1],
      ].map(({ id, industry, level }) => {
        return {
          industry,
          level,
          occupation: idToRolesMap.has(id)
            ? { __typename: 'LearnerProfile_OccupationId' as const, id }
            : { __typename: 'LearnerProfile_UserGeneratedOccupation' as const, name: id },
        };
      });

      const updatedFields: Partial<Profile> = showPreferredWorkLocationFields
        ? {
            openToRemoteWork: isOpenToRemoteWork,
            willingToRelocate: isWillingToRelocate,
            occupationPreferences: formattedOccupationObjects,
          }
        : {
            occupationPreferences: formattedOccupationObjects,
          };
      await updateLearnerProfile(updatedFields);

      setOnSaveError(undefined);
      setRolePreferenceErrors({});
      setIsLoading(false);
      onClose();
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'Error saving work preferences',
        },
      });
      setIsLoading(false);
      setOnSaveError(_t('Something went wrong. Please try again'));
      scrollToFirstError({ hasSaveError: true });
    }
  };

  const { HeadingGroup, Content, Actions } = Dialog;

  return (
    <Dialog open={isOpen} onClose={handleOnClose}>
      <HeadingGroup
        supportText={
          enableHiringSolutionsOptInFlow
            ? _t(
                "Let recruiters know what role you're looking for to make sure you find opportunities that are right for you."
              )
            : undefined
        }
      >
        {_t('Work preferences')}
      </HeadingGroup>
      <Content>
        <TrackedDiv
          trackClicks={false}
          withVisibilityTracking
          // not tracked in eventing v3
          trackingName="profile_work_preferences_dialog"
          requireFullyVisible={false}
        >
          {selectedRolesMap.map((rolePreference, idx) => {
            const isTalentPipelineRole = allTalentPipelineIds.has(rolePreference.id ?? '');
            const isFirstSelectedCHSRole = allTalentPipelineIds.has(rolePreference?.id ?? '');

            let selectedIndustry;
            if (rolePreference.industry) {
              selectedIndustry =
                'id' in rolePreference.industry ? rolePreference.industry.id : rolePreference.industry.name;
            }

            return (
              // Since the id/name changes whenever we select a new value, we can't use that as a key because it will cause the component to unount
              <div key={rolePreference.uniqueId} css={styles.withMarginBottom}>
                <RolePreferencesSelect
                  // We don't need to pass a ref to every single role preference with an error, since we only care about scrolling to the very first one
                  ref={(el) => {
                    rolePreferenceRefs.current[idx] = el;
                  }}
                  errorMessage={rolePreferenceErrors[rolePreference.uniqueId]}
                  placeholder={
                    rolePreference.id
                      ? idToRolesMap.get(rolePreference.id)?.occupationName ?? rolePreference.id
                      : _t('Select a role')
                  }
                  onSelectRole={(newId: string) => handleOnSelectRole(newId, idx)}
                  onSelectLevel={(level: LearnerProfileOccupationPreferenceLevel) =>
                    handleOnSelectLevel(idx, rolePreference.id ?? '', level)
                  }
                  onSelectIndustry={(value, isKey) => {
                    handleOnSelectIndustry(idx, rolePreference.id ?? '', value, isKey);
                  }}
                  onDeleteRole={hasOptionToDeleteRoles ? () => onDeleteDesiredRole(idx) : undefined}
                  selectedIndustry={selectedIndustry}
                  selectedLevel={
                    rolePreference.level === LearnerProfileOccupationPreferenceLevel.NotApplicable
                      ? undefined
                      : rolePreference.level
                  }
                  availableTalentPipelineRoleOptions={activelyHiringOccupationOptions}
                  availableRoleLevels={!rolePreference.id || isTalentPipelineRole ? [] : PREFERENCE_LEVELS}
                  shouldRenderLevelsSelect={Boolean(!isTalentPipelineRole && rolePreference.id)}
                  idx={idx}
                />
                {isFirstSelectedCHSRole && enableHiringSolutionsOptInFlow && (
                  <div css={styles.currentlyHiring}>
                    <CurrentlyHiringNotification
                      roleName={idToRolesMap.get(rolePreference.id ?? '')?.occupationName ?? ''}
                    />
                  </div>
                )}
                {idx < selectedRolesMap.length - 1 && <Divider color="dark" css={styles.divider} />}
              </div>
            );
          })}
          {selectedRolesMap.length < MAX_DESIRED_ROLES &&
            (selectedRolesMap?.[selectedRolesMap?.length - 1]?.id ||
              selectedRolesMap?.[selectedRolesMap?.length - 1]?.name) && (
              <Button
                variant="ghost"
                iconPosition="before"
                icon={<AddIcon />}
                edgeAlign="start"
                css={showPreferredWorkLocationFields ? styles.withMarginBottom : undefined}
                onClick={handleOnAdd}
                component={TrackedButton}
                // not tracked in eventing v3
                trackingName="profile_add_work_preferences_role"
                requireFullyVisible={false}
                withVisibilityTracking={true}
              >
                {_t('Add another role')}
              </Button>
            )}
          {showPreferredWorkLocationFields && (
            <>
              <Divider color="dark" />
              <div>
                <CheckboxGroup
                  label={_t('Are you open to working remotely?')}
                  columns={2}
                  value={isOpenToRemoteWork == null ? [] : [isOpenToRemoteWork.toString()]}
                  onChange={(e) => setIsOpenToRemoteWork(e.target.value === 'true')}
                  css={styles.checkboxGroup}
                  data-testid="open-to-remote-work-checkbox"
                >
                  {/* Checkbox values have to be a string */}
                  <CheckboxCard value="true" label={_t('Yes')} />
                  <CheckboxCard value="false" label={_t('No')} />
                </CheckboxGroup>
              </div>
              <div>
                <CheckboxGroup
                  label={_t('Are you willing to relocate within your home country?')}
                  columns={2}
                  value={isWillingToRelocate == null ? [] : [isWillingToRelocate.toString()]}
                  onChange={(e) => setIsWillingToRelocate(e.target.value === 'true')}
                  css={styles.checkboxGroup}
                  data-testid="willing-to-relocate-checkbox"
                >
                  <CheckboxCard value="true" label={_t('Yes')} />
                  <CheckboxCard value="false" label={_t('No')} />
                </CheckboxGroup>
              </div>
            </>
          )}
        </TrackedDiv>

        {onSaveError && (
          <InlineNotification css={styles.saveError} ref={saveErrorRef} severity="error">
            {onSaveError}
          </InlineNotification>
        )}
      </Content>
      <Actions ref={optOutOfTalentPipelineRef}>
        <Button
          onClick={handleOnSave}
          loading={isLoading}
          component={TrackedButton}
          // not tracked in eventing v3
          trackingName="profile_save_work_preferences"
          data-e2e={E2E_SELECTORS.WORK_PREF_DIALOG_SAVE}
          requireFullyVisible={false}
          withVisibilityTracking
        >
          {_t('Save')}
        </Button>
      </Actions>
    </Dialog>
  );
};

const ErrorBoundEditWorkPreferencesModal = (props: Props) => (
  <ErrorBoundaryWithTagging
    componentName="EditWorkPreferencesModal"
    errorComponent={
      <InlineNotification severity="error" title={_t('There has been an error')} role="alert">
        {_t('There has been an error in the edit work preferences modal. Our development team will be alerted.')}
      </InlineNotification>
    }
  >
    <EditWorkPreferencesModal {...props} />
  </ErrorBoundaryWithTagging>
);

export default ErrorBoundEditWorkPreferencesModal;
